import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';

/**
 * Tells the user when a newer build of the app has been deployed, so they can refresh onto it.
 *
 * A deploy does not reach an open tab on its own: the browser keeps running the bundle it loaded until
 * the page is reloaded. This polls /version.json, which build/post-build.js writes with the main
 * bundle's hash, and compares it with the hash post-build.js stamped into this bundle. When they
 * differ, updateAvailable turns on and the app shows a refresh banner (app-new-base).
 *
 * It never signs the user out or reloads by itself, so nobody loses work they have not saved. The
 * user refreshes when ready, and a refresh keeps their session.
 */
@Injectable({
  providedIn: 'root'
})
export class VersionCheckService {

  // Replaced with the build's hash by build/post-build.js.
  private currentHash = '{{POST_BUILD_ENTERS_HASH_HERE}}';

  /** True once a newer build than the running one has been deployed. */
  public readonly updateAvailable = signal(false);

  constructor(private http: HttpClient) { }

  /**
   * Checks now, every `frequency` milliseconds (default 10 minutes), and whenever the tab becomes
   * visible again, so a tab left open overnight learns of a deploy as soon as someone returns to it.
   */
  public initVersionCheck(url: string, frequency = 1000 * 60 * 10) {
    setInterval(() => this.checkVersion(url), frequency);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.checkVersion(url);
      }
    });
    this.checkVersion(url);
  }

  /** Loads the newer build. Called from the banner's Refresh button. */
  public reload() {
    window.location.reload();
  }

  private checkVersion(url: string) {
    if (this.updateAvailable()) {
      return;
    }
    // Timestamped to get past any cache.
    this.http.get<{ hash: string }>(url + '?t=' + new Date().getTime())
      .subscribe({
        next: response => {
          if (this.hasHashChanged(this.currentHash, response?.hash)) {
            this.updateAvailable.set(true);
          }
        },
        error: err => console.error(err, 'Could not get version')
      });
  }

  /**
   * A bundle without a stamped hash (a local build) never reports a change. The placeholder is
   * recognised by its braces, not by the full string: post-build.js replaces every copy of that
   * string, so a comparison against it would compare the hash with itself.
   */
  private hasHashChanged(currentHash: string, newHash: string): boolean {
    if (!currentHash || currentHash.startsWith('{{') || !newHash) {
      return false;
    }
    return currentHash !== newHash;
  }
}
